import {
  buildSinglePayOptionValue,
  getPaymentCardId,
  PaymentCard,
} from './paymentCards';
import {
  PublicationFormValues,
  PublicationMarketplace,
} from '../components/PublicationFormModal/types';
import {
  CreatePublicationPayload,
  EditPublicationPayload,
} from './types';

const STATUS_TO_STATE_ID: Record<string, number> = {
  activa: 1,
  inactiva: 2,
};

const CURRENCY_TO_ID: Record<string, number> = {
  ARS: 1,
};

const parseNumber = (value: string, fallback = 0): number => {
  const parsed = Number(value);
  return Number.isNaN(parsed) ? fallback : parsed;
};

const resolveCurrencyId = (currency: string): number => {
  const parsed = Number(currency);
  if (!Number.isNaN(parsed) && parsed > 0) return parsed;
  return CURRENCY_TO_ID[currency] ?? 1;
};

const parseCardIds = (cardIds: string[]): number[] =>
  cardIds
    .map((cardId) => Number(cardId))
    .filter((id) => !Number.isNaN(id));

const buildConfigValues = (
  values: PublicationFormValues,
  paymentCards: PaymentCard[] = [],
): Record<string, unknown> => {
  const cardIds = parseCardIds(values.oneePaymentCards);
  const singlePayLabels = cardIds
    .map((cardId) => {
      const card = paymentCards.find((item) => getPaymentCardId(item) === cardId);
      return card ? buildSinglePayOptionValue(card) : null;
    })
    .filter((value): value is string => value != null);

  return {
    card_id: cardIds.length === 1 ? cardIds[0] : null,
    card_ids: cardIds.map(String),
    showPrice: {
      main_price: values.mainPrice === 'cuotas',
      alter_price: values.alterPrice === 'unpago',
      main_amount: values.mainAmount === 'base' ? 'original_amount' : 'amount',
      alter_amount: values.alterAmount === 'base' ? 'original_amount' : 'amount',
    },
    searchable: true,
    bank_config: 'Default',
    singlePayOption: singlePayLabels.length ? singlePayLabels.join(', ') : null,
  };
};

export const mapFormToCreatePayload = (
  values: PublicationFormValues,
  marketplace: PublicationMarketplace,
  paymentCards: PaymentCard[] = [],
): CreatePublicationPayload => {
  const payload: CreatePublicationPayload = {
    marketplace,
    publication_state_id: STATUS_TO_STATE_ID[values.status] ?? 1,
    config_values_default: buildConfigValues(values, paymentCards),
    relevance: parseNumber(values.relevance),
    price_pvp: parseNumber(values.pvpPrice),
    product_id: parseNumber(values.productId),
    campaign_ids: values.campaigns.map(Number),
    campaign_price: parseNumber(values.pvCampaignPrice),
    target_ids: values.targets.map(Number),
    zone_ids: values.zones,
    sku: values.sku
  };

  if (marketplace === 'pipestore') {
    payload.currency_id = resolveCurrencyId(values.currency);
    payload.origin_id = parseNumber(values.origin);
    payload.company_id = parseNumber(values.company);
  }

  return payload;
};

export const mapFormToEditPayload = (
  values: PublicationFormValues,
  marketplace: PublicationMarketplace,
  paymentCards: PaymentCard[] = [],
): EditPublicationPayload => {
  const payload: EditPublicationPayload = {
    publicationId: values.publicationId,
    marketplace,
    publicationStateId: STATUS_TO_STATE_ID[values.status] ?? 1,
    relevance: parseNumber(values.relevance),
    configValues: buildConfigValues(values, paymentCards),
    pvpPrice: parseNumber(values.pvpPrice),
    stockQty: parseNumber(values.stockQty),
    alarmBase: parseNumber(values.alarmBase, 10),
    alarmMin: parseNumber(values.alarmMin, 2),
    campaignIds: values.campaigns.map(Number),
    pvCampaignPrice: parseNumber(values.pvCampaignPrice),
    targetIds: values.targets.map(Number),
    zoneIds: values.zones,
    variantGroupId: null,
    variant: null,
  };

  if (marketplace === 'pipestore') {
    payload.currencyId = resolveCurrencyId(values.currency);
    payload.companyId = parseNumber(values.company);
    payload.originId = parseNumber(values.origin);
  }

  return payload;
};
